import type { ReactElement } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { MiddleTruncatedPath } from '@/components/shared/MiddleTruncatedPath';
import type { ShadowAiToolUserProcess } from '@kirin/api-client/contracts/shadow-ai';

/** How many paths to show before collapsing the rest into a "+N more" hint. */
const MAX_VISIBLE_PATHS = 3;

/**
 * KIR-4650: the "Path" cell of the Installed AI Tools drill-down.
 *
 * Generic detection rules — "MCP CLI" matches any binary named `mcp` — give a
 * customer a finding with nothing in it they can act on. This cell is what
 * makes such a match reviewable: the paths the detection actually matched on,
 * plus the live process behind it when the tool happens to be running.
 *
 * The command line reaching this component was already stripped of secrets on
 * the endpoint, before upload (the agent's `RedactCommandLineSecrets`), so it
 * is displayed as-is rather than redacted a second time here.
 */
export function DetectionEvidenceCell({
  paths,
  runningProcess,
}: {
  paths: string[];
  runningProcess?: ShadowAiToolUserProcess | null;
}): ReactElement {
  // Dedupe before slicing. The Go agent already merges repeats in
  // consolidateDetected, but nothing on the write path enforces it: the ingest
  // schema accepts duplicates, sanitizeArray only truncates, and the upsert
  // stores paths verbatim -- unlike sources, which it folds through
  // array_agg(DISTINCT). So the invariant rests on agent-side code alone.
  //
  // Deduping here rather than keying by index on purpose. An index key would
  // silence React's duplicate-key warning while still rendering the same
  // location twice and burning two of the MAX_VISIBLE_PATHS slots, pushing a
  // real path into "+N more" -- treating the warning instead of the defect.
  const uniquePaths = [...new Set(paths)];
  const visible = uniquePaths.slice(0, MAX_VISIBLE_PATHS);
  const hiddenCount = uniquePaths.length - visible.length;

  // An installed tool with neither a recorded path nor a live process is
  // precisely the uninformative finding this ticket is about, so say so
  // rather than leaving an empty cell that reads as a rendering bug.
  if (visible.length === 0 && !runningProcess) {
    return <span className="text-muted-foreground">—</span>;
  }

  return (
    <div className="space-y-1 min-w-0">
      {visible.map(path => (
        <MiddleTruncatedPath
          key={path}
          path={path}
          // Copying one path copies that path; the popover is per-entry.
          className="font-mono"
        />
      ))}
      {hiddenCount > 0 && (
        <span
          className="text-muted-foreground"
          title={uniquePaths.slice(MAX_VISIBLE_PATHS).join('\n')}
        >
          +{hiddenCount} more
        </span>
      )}
      {runningProcess && <RunningProcessChip process={runningProcess} />}
    </div>
  );
}

DetectionEvidenceCell.displayName = 'DetectionEvidenceCell';

/**
 * A compact "running now" chip. The full binary/command detail lives in the
 * tooltip: this sits inside a dense drill-down table, and a command line is
 * routinely far too long to inline without wrecking the row height.
 */
function RunningProcessChip({
  process,
}: {
  process: ShadowAiToolUserProcess;
}): ReactElement {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-center gap-1 cursor-default w-fit">
          <Badge variant="secondary" className="text-xs font-mono">
            PID {process.pid}
          </Badge>
          {process.isRoot && (
            <Badge variant="destructive" className="text-xs px-1 py-0">
              root
            </Badge>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-lg break-all font-mono">
        <div className="space-y-0.5">
          {process.binaryPath && <div>Binary: {process.binaryPath}</div>}
          {process.commandLine && <div>Command: {process.commandLine}</div>}
          {process.parentName && (
            <div>
              Parent: {process.parentName}
              {process.ppid != null && ` (PID ${process.ppid})`}
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
